import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import "../styles/AssessmentForm.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SUBMODALITIES_MAP = {
     USG: ["Sub1", "Sub2"],
     Mammography: ["Sub1", "Sub2"],
     BMD: ["Sub1", "Sub2"],
     CT: ["Sub1", "Sub2"],
     MRI: ["Sub1", "Sub2"],
};

export default function AssessmentEditForm({ onCancel, onSave }) {
     const { candidateId } = useParams();
     const submittingRef = useRef(false);
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const navigate = useNavigate();
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [status, setStatus] = useState('');
     const [isLoading, setIsLoading] = useState(true);
     const [modalities, setModalities] = useState([]);

     const [selectedModality, setSelectedModality] = useState("");
     const [currentSelections, setCurrentSelections] = useState({});
     const [resumeImage, setResumeImage] = useState(null);
     const [selectedRegions, setSelectedRegions] = useState({
          regionsData: {}, // Stores the branch selections
          regionsIds: {}   // Stores the IDs for each region-branch combination
     });

     const [personal, setPersonal] = useState({
          positionApplied: "",
          candidateName: "",
          mobile: "",
          email: "",
          qualification: "",
          registration: "",
          paediatric: "",
          totalExperience: "",
          department: "",
          vdcLocation: "",
          association_type: "",
          timing_from: "",
          timing_to: "",
          hr_status: "",
          user_status: "",
     });

     const [technical, setTechnical] = useState({
          CT: "",
          MRI: "",
          RadiographyReporting: "",
          USG: "",
          assessmentCT: "",
          assessmentMRI: "",
          assessmentRadiography: "",
          assessmentUSG: "",
          idCT: null,
          idMRI: null,
          idRadiography: null,
          idUSG: null
     });

     const [hiring, setHiring] = useState({
          decision: "",
     });

     const [notes, setNotes] = useState({
          overallNotes: "",
     });

     const [personality, setPersonality] = useState({
          attitude: "",
          jobKnowledge: "",
          communication: "",
          confidence: "",
          personality: "",
          flexibility: "",
          idAttitude: null,
          idJobKnowledge: null,
          idCommunication: null,
          idConfidenc: null,
          idPersonlity: null,
          idPersonlity: null,
     });

     const [errors, setErrors] = useState({});

     // Fetch candidate data
     useEffect(() => {
          const fetchCandidateData = async () => {
               try {
                    setIsLoading(true);
                    const response = await fetch(`${baseUrl}/getCandidateFullDetails/${candidateId}`, {
                         headers: {
                              "Authorization": `Bearer ${sessionStorage.getItem("token")}`,
                         },
                    });

                    if (!response.ok) {
                         throw new Error('Failed to fetch candidate data');
                    }

                    const result = await response.json();
                    console.log("API Response:", result); // Debug log

                    if (result.candidate) {
                         populateFormData(result);
                    }
               } catch (error) {
                    console.error("Error fetching candidate data:", error);
                    toast.error("Failed to load candidate data");
               } finally {
                    setIsLoading(false);
               }
          };

          if (candidateId) {
               fetchCandidateData();
          }
     }, [candidateId, baseUrl]);

     const populateFormData = (data) => {
          const candidate = data.candidate;

          // Personal details - mapping API fields to form fields
          setPersonal({
               positionApplied: candidate.position_applied || "",
               candidateName: candidate.candidate_name || "",
               mobile: candidate.mobile || "",
               email: candidate.email || "",
               qualification: candidate.education_qualification || "",
               registration: candidate.medical_registration === "Y" ? "Yes" : "No",
               paediatric: candidate.Paediatric === "Y" ? "Yes" : "No",
               totalExperience: candidate.total_experience || "",
               department: candidate.department || "",
               vdcLocation: candidate.vdc_location || "",
               association_type: candidate.association_type || "",
               timing_from: candidate.timing_from ? candidate.timing_from.substring(0, 5) : "09:00",
               timing_to: candidate.timing_to ? candidate.timing_to.substring(0, 5) : "17:00",
               hr_status: candidate.hr_status || "",
               user_status: candidate.user_status || "",
               resume_file: candidate.resume_file || ""

          });

          // Notes
          setNotes({
               overallNotes: candidate.overallNotes || "",
          });

          // Hiring decision
          setHiring({
               decision: candidate.hr_status || candidate.rpt_mgr_status || "Onhold",
          });

          // Modalities - transform API data to match form structure
          if (data.modalities && Array.isArray(data.modalities)) {
               const modalitiesGrouped = data.modalities.reduce((acc, item) => {
                    const modality = item.modality;
                    if (!acc[modality]) {
                         acc[modality] = {
                              modality: modality,
                              selections: {}
                         };
                    }
                    acc[modality].selections[item.selection_area] = {
                         applicable: item.applicable,
                         level: item.level,
                         id: item.id || null
                    };
                    return acc;
               }, {});

               setModalities(Object.values(modalitiesGrouped));
          }

          // Technical evaluations
          if (data.technical_evaluations && Array.isArray(data.technical_evaluations)) {
               const techData = data.technical_evaluations.reduce((acc, item) => {
                    switch (item.particular) {
                         case "Radiography Reporting":
                              acc.assessmentRadiography = item.assessment || "";
                              acc.RadiographyReporting = item.notes || "";
                              acc.idRadiography = item.id || null;
                              break;
                         case "CT Scans Reporting":
                              acc.assessmentCT = item.assessment || "";
                              acc.CT = item.notes || "";
                              acc.idCT = item.id || null;
                              break;
                         case "MRI Scans Reporting":
                              acc.assessmentMRI = item.assessment || "";
                              acc.MRI = item.notes || "";
                              acc.idMRI = item.id || null;
                              break;

                    }
                    return acc;
               }, {});
               setTechnical(prev => ({ ...prev, ...techData }));
          }

          // Personality assessments
          if (data.personality_assessments && Array.isArray(data.personality_assessments)) {
               const personalityData = data.personality_assessments.reduce((acc, item) => {
                    switch (item.criteria) {
                         case "Attitude":
                              acc.attitude = item.remarks || "";
                              acc.idAttitude = item.id || null;
                              break;
                         case "Job Knowledge":
                              acc.jobKnowledge = item.remarks || "";
                              acc.idJobKnowledge = item.id || null;
                              break;
                         case "Communication Skills":
                              acc.communication = item.remarks || "";
                              acc.idCommunication = item.id || null;
                              break;
                         case "Confidence":
                              acc.confidence = item.remarks || "";
                              acc.idConfidenc = item.id || null;
                              break;
                         case "Personality":
                              acc.personality = item.remarks || "";
                              acc.idPersonlity = item.id || null;
                              break;
                         case "Flexibility":
                              acc.flexibility = item.remarks || "";
                              acc.idFlexibility = item.id || null;
                              break;
                    }
                    return acc;
               }, {});
               setPersonality(prev => ({ ...prev, ...personalityData }));
          }

          // Regions - transform API data to match form structure
          // Regions - transform API data to match form structure
          if (data.regions && Array.isArray(data.regions)) {
               const regionsData = data.regions.reduce((acc, item) => {
                    const region = item.region_name;
                    if (!acc[region]) {
                         acc[region] = [];
                    }
                    acc[region].push(item.branch_name);
                    return acc;
               }, {});

               // Store IDs for each region-branch combination
               const regionsIds = data.regions.reduce((acc, item) => {
                    const key = `${item.region_name}-${item.branch_name}`;
                    acc[key] = item.id || null;
                    return acc;
               }, {});

               setSelectedRegions({
                    regionsData: regionsData,
                    regionsIds: regionsIds
               });
          }
     };

     // Rest of your functions remain the same...
     const handleModalityChange = (modality, submodality, data, id = null) => {
          setCurrentSelections((prev) => ({
               ...prev,
               [submodality]: {
                    ...prev[submodality],
                    ...data,
                    id: id || prev[submodality]?.id || null
               }
          }));
     };

     function handleAdd() {
          if (!selectedModality) return;

          const alreadyExists = modalities.some(
               (entry) => entry.modality === selectedModality
          );
          // if (alreadyExists) {
          //   alert(`${selectedModality} has already been added.`);
          //   return;
          // }

          const entry = {
               modality: selectedModality,
               selections: { ...currentSelections },
               // id: null 
          };

          setModalities((prev) => [...prev, entry]);
          setSelectedModality("");
          setCurrentSelections({});
     }

     function handlePersonalChange(e) {
          const { name, value } = e.target;
          setPersonal((p) => ({ ...p, [name]: value }));
     }

     function handleTechnicalChange(e) {
          const { name, value } = e.target;
          setTechnical((t) => ({ ...t, [name]: value }));
     }

     function handleHiringChange(e) {
          const { name, value } = e.target;
          setHiring((h) => ({ ...h, [name]: value }));
     }

     function handleNotesChange(e) {
          const { name, value } = e.target;
          setNotes((n) => ({ ...n, [name]: value }));
     }

     function handlePersonalityChange(e) {
          const { name, value } = e.target;
          setPersonality((p) => ({ ...p, [name]: value }));
     }

     const handleBranchToggle = (region, branch) => {
          setSelectedRegions(prev => {
               const current = prev.regionsData[region] || [];
               const updated = current.includes(branch)
                    ? current.filter(b => b !== branch)
                    : [...current, branch];

               return {
                    ...prev,
                    regionsData: { ...prev.regionsData, [region]: updated }
               };
          });
     };

     const handleSelectAll = (region, branches) => {
          setSelectedRegions((prev) => {
               const current = prev.regionsData[region] || [];
               const allSelected = branches.every((b) => current.includes(b));

               return {
                    ...prev,
                    regionsData: {
                         ...prev.regionsData,
                         [region]: allSelected ? [] : branches,
                    }
               };
          });
     };

     const handleDelete = (index) => {
          setModalities((prevModalities) => prevModalities.filter((_, i) => i !== index));
     };

     async function handleSubmit(e) {
          e.preventDefault();
          if (isSubmitting) return;
          setIsSubmitting(true);

          try {
               const formData = new FormData();

               formData.append("personal", JSON.stringify({
                    position_applied: personal.positionApplied,
                    candidate_name: personal.candidateName,
                    mobile: personal.mobile,
                    email: personal.email,
                    education_qualification: personal.qualification,
                    medical_registration: personal.registration === "Yes" ? "Y" : "N",
                    Paediatric: personal.paediatric === "Yes" ? "Y" : "N",
                    total_experience: personal.totalExperience,
                    department: personal.department,
                    vdc_location: personal.vdcLocation,
                    association_type: personal.association_type,
                    timing_from: personal.timing_from + ":00",
                    timing_to: personal.timing_to + ":00",
                    hr_status: personal.hr_status,
                    user_status: personal.user_status
               }));

               formData.append("modalities", JSON.stringify(
                    modalities.flatMap(entry =>
                         Object.entries(entry.selections).map(([selection_area, data]) => ({
                              id: data?.id || entry?.id,
                              modality: entry.modality,
                              selection_area,
                              applicable: data.applicable,
                              level: data.level
                         }))
                    )
               ));

               formData.append("technical_evaluations", JSON.stringify([
                    { id: technical.idRadiography, particular: "Radiography Reporting", assessment: technical.assessmentRadiography, notes: technical.RadiographyReporting },
                    { id: technical.idCT, particular: "CT Scans Reporting", assessment: technical.assessmentCT, notes: technical.CT },
                    { id: technical.idMRI, particular: "MRI Scans Reporting", assessment: technical.assessmentMRI, notes: technical.MRI }
               ]));

               formData.append("personality_assessments", JSON.stringify([
                    { id: personality.idAttitude, criteria: "Attitude", remarks: personality.attitude },
                    { id: personality.idCommunication, criteria: "Communication Skills", remarks: personality.communication },
                    { id: personality.idConfidenc, criteria: "Confidence", remarks: personality.confidence },
                    { id: personality.idPersonlity, criteria: "Personality", remarks: personality.personality },
                    { id: personality.idFlexibility, criteria: "Flexibility", remarks: personality.flexibility }
               ]));

               formData.append("regions", JSON.stringify(
                    Object.entries(selectedRegions.regionsData).flatMap(([region_name, branches]) =>
                         branches.map(branch_name => ({
                              region_name,
                              branch_name,
                              id: selectedRegions.regionsIds[`${region_name}-${branch_name}`] || ''
                         }))
                    )
               ));

               if (resumeImage) {
                    formData.append("resumeImage", resumeImage);
               }

               const response = await fetch(`${baseUrl}/updateCandidateDetailsId_check/${candidateId}`, {
                    method: "POST",
                    headers: {
                         "Authorization": `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: formData,
               });

               const result = await response.json();
               if (!response.ok) throw new Error(result.message || "Update failed");

               toast.success("Assessment updated successfully!");
               setTimeout(() => navigate("/new-radiology-assessment"), 1000);

          } catch (err) {
               toast.error(err.message);
          } finally {
               setIsSubmitting(false);
          }
     }


     function handleCancel() {
          navigate('/new-radiology-assessment');
     }

     // Your existing constants and styles...
     const personalityOptions = [
          "Outstanding",
          "Exceeds Expectation",
          "Meets Expectation",
          "Does not meet expectation",
     ];

     const cardStyle = {
          background: "#fff",
          padding: "5px",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgb(0 0 0 / 0.1)",
          marginBottom: "5px",
     };

     const grid4Columns = {
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "5px",
          alignItems: "start",
     };

     const grid6Columns = {
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: "16px",
          alignItems: "start",
     };


     if (isLoading) {
          return (
               <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    fontSize: '18px',
                    color: '#666'
               }}>
                    Loading candidate data...
               </div>
          );
     }

     const MODALITY_MAP = {
          XRAY: [
               "Plain Radiography - Basic",
               "Plain Radiography - Advanced",
               "Lower limb scanogram",
               "Whole spine X-ray",
               "Boneage"
          ],
          XRAYProcedure: [
               "Barium Study",
               "Distal Loopogram/ Fistulogram",
               "HSG",
               "IVP",
               "MCUG",
               "RGU"
          ],
          Mammography: [
               "Mammography",
               "Mammography with out screening"
          ],
          BMD: [
               "Mammography"
          ],
          USG: [
               "Basic",
               "Doppler Studies",
               "Thyroid",
               "Scrotum USG",
               "Breast USG",
               "TIFFA",
               "Foetal Echo",
               "MSK USG",
               "Penile Doppler",
               "TRUS",
               "USG Guided FNAC",
               "USG Guided Trucut biopsy",
               "USG Guided aspiration",
               "Breast Biopsy",
               "TRUS guided Biopsy"
          ],
          CT: [
               "Brain",
               "PNS",
               "Orbits",
               "Temporal Bone",
               "Neck",
               "KUB",
               "Spine",
               "Chest",
               "Abdomen",
               "DECT",
               "3D CT",
               "CT Angiography",
               "Coronary CT",
               "Virtual Bronchoscopy",
               "CT Perfusion",
               "CT Screening",
               "Bronchoscopy / Colonoscopy",
               "CT Guided Biopsy",
               "Virtual Bronchoscopy / Colonoscopy"
          ],
          MRI: [
               "Brain",
               "Spine",
               "Ankle",
               "Elbow",
               "Hip",
               "Knee",
               "Wrist",
               "Foot",
               "Shoulder",
               "SI Joint",
               "MSK -Small Joints",
               "MRI Extremities",
               // "Boss Protocol - PNS",
               "DBS Protocol",
               "Dernentia Protocol",
               "Epdepsy Protocol",
               "Headache Protocol",
               "BIH/lIH Protocol",
               "MRI Cochlear Implant Protocol",
               "Stroke Protocol",
               "Vertigo Protocol",
               "Visual Pathway Protocol",
               "Abdomen",
               "Brain with CSF",
               "Cardiac MRI",
               "Chest",
               "Dynamic Sleep Study",
               "Fibroid Mapping",
               "FMR + DTI + MRS",
               "FMRI",
               "Foetal MRI",
               "Lymphangiography",
               "Movement Disorder",
               "MRCP",
               "MRI Angiographyhain",
               "MRI Arhrogram",
               "MRI Breast",
               "MRI Cartigram",
               "MRI Cistenography",
               "MRI Defaccography",
               "MRI Enterography / MRI Enteroclysis",
               "MRI Face",
               "MRI Fistulogram",
               "MRI Liver",
               "MRI Neurography",
               "MRI Perfusion",
               "MRI Peripheral Angiography",
               "MRI Screcning",
               "MRI TM Joints",
               "MRI Tractography",
               "MRI Urography",
               // "MSK",
               "Neck",
               "Pelvis",
               "PNS",
               "Prostate",
               "Sella/Pituitary",
               "Soft Tissue MRI",
               "Spectroscopy",
               "Temporal Bone",
               "Vessel Wall",
               "OverallEvaluation Notes",
               "Whole Body"
          ],
          "Special Procedures": [
               "RGU",
               "MCUG",
               "Barium Swallow",
               "Barium Enema",
               "HSG",
          ],
     };


     return (
          <form onSubmit={handleSubmit}>
               <ToastContainer />
               <div style={{
                    minHeight: "100vh",
                    padding: 5,
                    overflowY: "auto",
                    backgroundColor: "#f9f9f9",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
               }}>
                    <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>

                         {/* Personal Details Section */}
                         <div className="form-section" style={cardStyle}>
                              <div className="section-title" style={{ marginBottom: "5px" }}>
                                   Personal Details
                              </div>
                              <div style={grid4Columns}>
                                   <label>
                                        Position Applied
                                        <input
                                             className="form-control input-form"
                                             name="positionApplied"
                                             value={personal.positionApplied}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Candidate Name
                                        <input
                                             className="form-control input-form"
                                             name="candidateName"
                                             value={personal.candidateName}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Mobile
                                        <input
                                             className="form-control input-form"
                                             name="mobile"
                                             type="text"
                                             value={personal.mobile}
                                             maxLength={10}
                                             required
                                             onChange={(e) => {
                                                  const onlyNums = e.target.value.replace(/\D/g, "");
                                                  handlePersonalChange({
                                                       target: { name: e.target.name, value: onlyNums },
                                                  });
                                             }}
                                        />
                                   </label>

                                   <label>
                                        Email
                                        <input
                                             className="form-control input-form"
                                             name="email"
                                             type="email"
                                             value={personal.email}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Educational Qualification
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="qualification"
                                             value={personal.qualification}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Department
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="department"
                                             value={personal.department}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Relevant State Medical Registration
                                        <select
                                             className="form-control input-form"
                                             required
                                             name="registration"
                                             value={personal.registration}
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option>Yes</option>
                                             <option>No</option>
                                        </select>
                                   </label>

                                   <label>
                                        Paediatric
                                        <select
                                             className="form-control input-form"
                                             required
                                             name="paediatric"
                                             value={personal.paediatric}
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option>Yes</option>
                                             <option>No</option>
                                        </select>
                                   </label>

                                   <label>
                                        Total Experience (In Yrs.)
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="totalExperience"
                                             value={personal.totalExperience}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        VDC Location (Applied for)
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="vdcLocation"
                                             value={personal.vdcLocation}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Timings From
                                        <input
                                             required
                                             type="time"
                                             className="form-control input-form"
                                             name="timing_from"
                                             value={personal.timing_from}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Timings To
                                        <input
                                             required
                                             type="time"
                                             className="form-control input-form"
                                             name="timing_to"
                                             value={personal.timing_to}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Type of Association
                                        <select
                                             className="form-control input-form"
                                             name="association_type"
                                             value={personal.association_type}
                                             required
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option value="Full Time">Full Time</option>
                                             <option value="Part Time">Part Time</option>
                                             <option value="Target Model">Target Model</option>
                                             <option value="Base">Base</option>
                                        </select>
                                   </label>

                                   <label>Hr Status
                                        <select
                                             className="form-control input-form"
                                             name="hr_status"
                                             value={personal.hr_status}
                                             onChange={(e) => setPersonal(prev => ({ ...prev, hr_status: e.target.value }))}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                             disabled
                                        >
                                             <option value="">Select Status</option>
                                             <option value="Pending">Pending</option>
                                             <option value="Approved">Approved</option>
                                        </select>
                                   </label>

                                   <label>User Status
                                        <select
                                             className="form-control input-form"
                                             name="user_status"
                                             value={personal.user_status}
                                             onChange={(e) => setPersonal(prev => ({ ...prev, user_status: e.target.value }))}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">Select Status</option>
                                             <option value="Pending">Pending</option>
                                             <option value="Approved">Approved</option>
                                        </select>
                                   </label>

                                   <label>
                                        Upload Image
                                        <input
                                             type="file"
                                             className="form-control input-form"
                                             accept="image/*"
                                             onChange={(e) => setResumeImage(e.target.files[0])}
                                        />
                                   </label>

                                   {/* Show existing resume only if available */}
                                   {personal.resume_file && (
                                        <div style={{ marginTop: "6px" }}>
                                             <a
                                                  href={`${process.env.REACT_APP_FILE_URL}/uploads/candidate_resume/${personal.resume_file}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                       color: "#007bff",
                                                       textDecoration: "underline"
                                                  }}
                                             >
                                                  View Uploaded Image
                                             </a>

                                             {/* Optional thumbnail preview for images */}
                                             {personal.resume_file.match(/\.(jpg|jpeg|png|gif)$/i) && (
                                                  <img
                                                       src={`${process.env.REACT_APP_FILE_URL}/uploads/candidate_resume/${personal.resume_file}`}
                                                       alt="Resume"
                                                       style={{
                                                            display: "block",
                                                            marginTop: "8px",
                                                            width: "120px",
                                                            height: "auto",
                                                            border: "1px solid #ddd",
                                                            borderRadius: "4px"
                                                       }}
                                                  />
                                             )}
                                        </div>
                                   )}
                              </div>
                         </div>

                         {/* Add the rest of your form sections here (Modalities, Technical Evaluation, Regions, Personality Assessment) */}
                         {/* ... your existing form sections ... */}
                         <div className="form-section" style={{ marginBottom: "24px" }}>
                              <div className="section-title" style={{ fontSize: "18px", fontWeight: "600", marginBottom: "12px" }}>
                                   Assessment of Relevant Modalities
                              </div>

                              {/* Modality Dropdown */}
                              <label style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                                   <span style={{ fontWeight: "500", marginRight: "8px" }}>Modality:</span>
                                   <select
                                        className="form-control input-form"
                                        value={selectedModality}
                                        onChange={(e) => {
                                             setSelectedModality(e.target.value);
                                             setCurrentSelections({});
                                        }}
                                        style={{
                                             height: "36px",
                                             width: "300px",
                                             padding: "4px 12px",
                                             borderRadius: "6px",
                                             border: "1px solid #ccc",
                                             fontSize: "14px"
                                        }}
                                   >
                                        <option value="">-- Select Modality --</option>
                                        {Object.keys(MODALITY_MAP).map((modality) => (
                                             <option key={modality} value={modality}>
                                                  {modality}
                                             </option>
                                        ))}
                                   </select>
                              </label>

                              {errors["modality"] && (
                                   <div className="field-error" style={{ color: "red", fontSize: "13px", marginTop: "-8px", marginBottom: "8px" }}>
                                        {errors["modality"]}
                                   </div>
                              )}

                              {selectedModality && (
                                   <div className="submodality-table" style={{ marginTop: "8px" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "16px" }}>
                                             <thead style={{ backgroundColor: "#f8f9fa" }}>
                                                  <tr>
                                                       {["Modality", "Items", "Applicable", "Intermediate", "Expert"].map((heading) => (
                                                            <th
                                                                 key={heading}
                                                                 style={{
                                                                      padding: "12px",
                                                                      textAlign: "center",
                                                                      fontSize: "14px",
                                                                      fontWeight: 600,
                                                                      borderBottom: "1px solid #dee2e6",
                                                                      verticalAlign: "middle",
                                                                 }}
                                                            >
                                                                 {heading}
                                                            </th>
                                                       ))}
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {Array.isArray(MODALITY_MAP[selectedModality]) &&
                                                       MODALITY_MAP[selectedModality].map((sub, idx) => {
                                                            const existingEntry = modalities.find(m =>
                                                                 m.modality === selectedModality && m.selections[sub]
                                                            );
                                                            const existingId = existingEntry?.selections[sub]?.id || null;
                                                            const isApplicable = currentSelections[sub]?.applicable === "YES";
                                                            const selectedLevel = currentSelections[sub]?.level;

                                                            return (
                                                                 <tr key={sub} style={{ lineHeight: "0px" }}>
                                                                      {/* Merge cell for Modality name */}
                                                                      {idx === 0 && (
                                                                           <td
                                                                                rowSpan={MODALITY_MAP[selectedModality].length}
                                                                                style={{
                                                                                     padding: "12px",
                                                                                     fontWeight: "500",
                                                                                     borderBottom: "1px solid #dee2e6",
                                                                                     backgroundColor: "#fdfdfd",
                                                                                     textAlign: "center",
                                                                                     verticalAlign: "middle"
                                                                                }}
                                                                           >
                                                                                {selectedModality}
                                                                           </td>
                                                                      )}

                                                                      {/* Submodality name */}
                                                                      <td
                                                                           style={{
                                                                                padding: "12px",
                                                                                borderBottom: "1px solid #dee2e6",
                                                                                verticalAlign: "middle"
                                                                           }}
                                                                      >
                                                                           {sub}
                                                                      </td>

                                                                      {/* Applicable Yes/No */}
                                                                      <td
                                                                           style={{
                                                                                padding: "12px",
                                                                                borderBottom: "1px solid #dee2e6",
                                                                                textAlign: "center",
                                                                                verticalAlign: "middle"
                                                                           }}
                                                                      >
                                                                           <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
                                                                                <label style={{ display: "flex", alignItems: "center", gap: "6px", paddingTop: "5px" }}>
                                                                                     Yes
                                                                                </label>
                                                                                <input
                                                                                     type="radio"
                                                                                     name={`applicable-${selectedModality}-${sub}`}
                                                                                     value="YES"
                                                                                     onChange={() =>
                                                                                          handleModalityChange(selectedModality, sub, {
                                                                                               applicable: "YES",
                                                                                               level: ""
                                                                                          }, existingId)
                                                                                     }
                                                                                     checked={currentSelections[sub]?.applicable === "YES"}
                                                                                />
                                                                                <label style={{ display: "flex", alignItems: "center", gap: "6px", paddingTop: "5px" }}>
                                                                                     No
                                                                                </label>
                                                                                <input
                                                                                     type="radio"
                                                                                     name={`applicable-${selectedModality}-${sub}`}
                                                                                     value="NO"
                                                                                     onChange={() =>
                                                                                          handleModalityChange(selectedModality, sub, {
                                                                                               applicable: "NO",
                                                                                               level: ""
                                                                                          }, existingId)
                                                                                     }
                                                                                     checked={currentSelections[sub]?.applicable === "NO"}
                                                                                />
                                                                           </div>
                                                                      </td>

                                                                      {/* Skill Level Options */}
                                                                      {["Intermediate", "Expert"].map((level) => (
                                                                           <td
                                                                                key={level}
                                                                                style={{
                                                                                     padding: "12px",
                                                                                     borderBottom: "1px solid #dee2e6",
                                                                                     textAlign: "center",
                                                                                     verticalAlign: "middle",
                                                                                     opacity: isApplicable ? 1 : 0.4,
                                                                                     pointerEvents: isApplicable ? "auto" : "none"
                                                                                }}
                                                                           >
                                                                                <input
                                                                                     type="radio"
                                                                                     name={`level-${selectedModality}-${sub}`}
                                                                                     value={level}
                                                                                     onChange={() =>
                                                                                          handleModalityChange(selectedModality, sub, {
                                                                                               applicable: currentSelections[sub]?.applicable || "YES",
                                                                                               level
                                                                                          }, existingId)
                                                                                     }
                                                                                     checked={selectedLevel === level}
                                                                                     disabled={!isApplicable}
                                                                                />
                                                                           </td>
                                                                      ))}
                                                                 </tr>
                                                            );
                                                       })}
                                             </tbody>
                                        </table>

                                        {/* + Add button */}
                                        <button
                                             type="button"
                                             onClick={handleAdd}
                                             disabled={Object.keys(currentSelections).length === 0}
                                             style={{
                                                  padding: "8px 16px",
                                                  backgroundColor: "#007bff",
                                                  color: "#fff",
                                                  border: "none",
                                                  borderRadius: "4px",
                                                  fontWeight: "500",
                                                  fontSize: "14px",
                                                  cursor: Object.keys(currentSelections).length === 0 ? "not-allowed" : "pointer",
                                                  opacity: Object.keys(currentSelections).length === 0 ? 0.6 : 1
                                             }}
                                        >
                                             + Add
                                        </button>
                                   </div>
                              )}

                              {/* Display added modalities */}
                              <div style={{ marginTop: "24px" }}>
                                   <h6 style={{ fontWeight: "600", fontSize: "16px", marginBottom: "12px" }}>Added Modalities</h6>
                                   {!Array.isArray(modalities) || modalities.length === 0 ? (
                                        <p style={{ color: "#6c757d" }}>No modalities added yet.</p>
                                   ) : (
                                        modalities.map((entry, idx) => {
                                             // Check if this is an existing modality (has any ID in its selections)
                                             const hasExistingIds = Object.values(entry.selections).some(
                                                  data => data?.id !== undefined && data?.id !== null
                                             );
                                             const isExistingModality = hasExistingIds;
                                             const isNewlyAdded = !isExistingModality;

                                             return (
                                                  <div
                                                       key={idx}
                                                       style={{
                                                            marginBottom: "16px",
                                                            padding: "12px",
                                                            border: "1px solid #dee2e6",
                                                            borderRadius: "6px",
                                                            backgroundColor: "#f8f9fa",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "flex-start",
                                                            position: "relative"
                                                       }}
                                                  >
                                                       <div style={{ flex: 1 }}>
                                                            <strong style={{ fontSize: "15px" }}>{entry.modality}</strong>
                                                            {isExistingModality && (
                                                                 <span style={{
                                                                      fontSize: "12px",
                                                                      color: "#28a745",
                                                                      marginLeft: "10px",
                                                                      fontWeight: "normal"
                                                                 }}>
                                                                      (Existing)
                                                                 </span>
                                                            )}
                                                            {isNewlyAdded && (
                                                                 <span style={{
                                                                      fontSize: "12px",
                                                                      color: "#007bff",
                                                                      marginLeft: "10px",
                                                                      fontWeight: "normal"
                                                                 }}>
                                                                      (Newly Added)
                                                                 </span>
                                                            )}
                                                            <ul style={{ paddingLeft: "18px", marginTop: "6px", marginBottom: 0 }}>
                                                                 {Object.entries(entry.selections).map(([submodality, data]) => (
                                                                      <li key={submodality}>
                                                                           {submodality}: {data?.applicable === "YES" ? data.level : "Not Applicable"}
                                                                      </li>
                                                                 ))}
                                                            </ul>
                                                       </div>

                                                       {/* Show delete button only for newly added modalities */}
                                                       {isNewlyAdded && (
                                                            <button
                                                                 onClick={() => handleDelete(idx)}
                                                                 style={{
                                                                      backgroundColor: "#dc3545",
                                                                      color: "#fff",
                                                                      border: "none",
                                                                      borderRadius: "4px",
                                                                      padding: "6px 10px",
                                                                      fontSize: "13px",
                                                                      fontWeight: "500",
                                                                      cursor: "pointer",
                                                                      height: "fit-content"
                                                                 }}
                                                                 aria-label={`Delete ${entry.modality}`}
                                                            >
                                                                 Delete
                                                            </button>
                                                       )}

                                                       {/* For existing modalities, show a disabled message */}
                                                       {isExistingModality && (
                                                            <span style={{
                                                                 fontSize: "12px",
                                                                 color: "#6c757d",
                                                                 fontStyle: "italic"
                                                            }}>
                                                                 Cannot delete existing modality
                                                            </span>
                                                       )}
                                                  </div>
                                             );
                                        })
                                   )}
                              </div>
                         </div>

                         <div
                              style={{
                                   display: "grid",
                                   gridTemplateColumns: "1fr 1fr",
                                   gap: 39,
                                   marginBottom: 5,
                                   flexWrap: "wrap",
                              }}
                         >
                              {/* Technical Evaluation */}
                              <div className="form-section" style={{ minWidth: 0, width: "100%" }}>
                                   <div className="section-title">Technical Evaluation</div>
                                   <div
                                        style={{
                                             display: "grid",
                                             gridTemplateColumns: "1fr 200px 1fr",
                                             columnGap: 16,
                                             rowGap: 8,
                                             alignItems: "center",
                                             width: "100%",
                                        }}
                                   >
                                        {/* Header row */}
                                        <div><strong>Type</strong></div>
                                        <div><strong>Assessment</strong></div>
                                        <div><strong>Notes</strong></div>

                                        {/* Radiography Reporting */}
                                        <div>Radiography Reporting</div>
                                        <div>
                                             <select
                                                  className="form-control input-form"
                                                  required
                                                  name="assessmentRadiography"
                                                  value={technical.assessmentRadiography || ""}
                                                  onChange={handleTechnicalChange}
                                                  style={{ height: "32px", width: "100%", padding: "0 12px" }}
                                             >
                                                  <option value="">-- Select --</option>
                                                  <option value="Good">Good</option>
                                                  <option value="Satisfactory">Satisfactory</option>
                                                  <option value="Dissatisfactory">Dissatisfactory</option>
                                                  <option value="NotApplicable">Not Applicable</option>
                                             </select>
                                             <div className="field-error">{errors["technical.assessmentRadiography"]}</div>
                                        </div>
                                        <div>
                                             <input
                                                  className="form-control input-form"
                                                  name="RadiographyReporting"
                                                  value={technical.RadiographyReporting}
                                                  onChange={handleTechnicalChange}
                                                  required
                                             />
                                             <div className="field-error">{errors["technical.RadiographyReporting"]}</div>
                                        </div>

                                        {/* CT Scans Reporting */}
                                        <div>CT Scans Reporting</div>
                                        <div>
                                             <select
                                                  className="form-control input-form"
                                                  required
                                                  name="assessmentCT"
                                                  value={technical.assessmentCT || ""}
                                                  onChange={handleTechnicalChange}
                                                  style={{ height: "32px", width: "100%", padding: "0 12px" }}
                                             >
                                                  <option value="">-- Select --</option>
                                                  <option value="Good">Good</option>
                                                  <option value="Satisfactory">Satisfactory</option>
                                                  <option value="Dissatisfactory">Dissatisfactory</option>
                                                  <option value="NotApplicable">Not Applicable</option>
                                             </select>
                                             <div className="field-error">{errors["technical.assessmentCT"]}</div>
                                        </div>
                                        <div>
                                             <input
                                                  className="form-control input-form"
                                                  required
                                                  name="CT"
                                                  value={technical.CT}
                                                  onChange={handleTechnicalChange}
                                             />
                                             <div className="field-error">{errors["technical.CT"]}</div>
                                        </div>

                                        {/* MRI Scans Reporting */}
                                        <div>MRI Scans Reporting</div>
                                        <div>
                                             <select
                                                  className="form-control input-form"
                                                  required
                                                  name="assessmentMRI"
                                                  value={technical.assessmentMRI || ""}
                                                  onChange={handleTechnicalChange}
                                                  style={{ height: "32px", width: "100%", padding: "0 12px" }}
                                             >
                                                  <option value="">-- Select --</option>
                                                  <option value="Good">Good</option>
                                                  <option value="Satisfactory">Satisfactory</option>
                                                  <option value="Dissatisfactory">Dissatisfactory</option>
                                                  <option value="NotApplicable">Not Applicable</option>
                                             </select>
                                             <div className="field-error">{errors["technical.assessmentMRI"]}</div>
                                        </div>
                                        <div>
                                             <input
                                                  className="form-control input-form"
                                                  required
                                                  name="MRI"
                                                  value={technical.MRI}
                                                  onChange={handleTechnicalChange}
                                             />
                                             <div className="field-error">{errors["technical.MRI"]}</div>
                                        </div>

                                        {/* USG Scanning */}
                                        {/* <div>USG Scanning</div>
                <div>
                  <select
                    className="form-control input-form"
                    required
                    name="assessmentUSG"
                    value={technical.assessmentUSG || ""}
                    onChange={handleTechnicalChange}
                    style={{ height: "32px", width: "100%", padding: "0 12px" }}
                  >
                    <option value="">-- Select --</option>
                    <option value="Good">Good</option>
                    <option value="Satisfactory">Satisfactory</option>
                    <option value="Dissatisfactory">Dissatisfactory</option>
                    <option value="NotApplicable">Not Applicable</option>
                  </select>
                  <div className="field-error">{errors["technical.assessmentUSG"]}</div>
                </div>
                <div>
                  <input
                    className="form-control input-form"
                    required
                    name="USG"
                    value={technical.USG}
                    onChange={handleTechnicalChange}
                  />
                  <div className="field-error">{errors["technical.USG"]}</div>
                </div> */}
                                   </div>
                              </div>



                              <div className="accordion-section">
                                   <div className="accordion-content">
                                        <div className="section-title" style={{ marginBottom: "5px" }}>
                                             Regions
                                        </div>
                                        <div className="branch-access-container">
                                             <p className="branch-access-description">
                                                  Select the regions and branches where access should be granted:
                                             </p>

                                             {errors["regions"] && (
                                                  <div className="field-error" style={{ marginTop: "8px" }}>
                                                       {errors["regions"]}
                                                  </div>
                                             )}

                                             <div className="branch-access-grid">

                                                  {/* AP & Telangana */}
                                                  <div className="region-card">
                                                       <div className="region-card-header">
                                                            <label className="region-select-all">
                                                                 <input
                                                                      type="checkbox"
                                                                      checked={
                                                                           (selectedRegions.regionsData["AP & Telangana"] || []).length === 79
                                                                      }
                                                                      onChange={() =>
                                                                           handleSelectAll("AP & Telangana", [
                                                                                "A S Rao Nagar",
                                                                                "Akruthi Township",
                                                                                "Alwal",
                                                                                "Ameerpet",
                                                                                "Attapur",
                                                                                "Balapur",
                                                                                "Bandlaguda",
                                                                                "BHEL",
                                                                                "BN Reddy Nagar",
                                                                                "Boduppal",
                                                                                "Chintal",
                                                                                "Dammaiguda",
                                                                                "Dilsukhnagar",
                                                                                "Dwarakanagar",
                                                                                "Film Nagar",
                                                                                "Gachibowli",
                                                                                "Gajuwaka",
                                                                                "Gopalapatnam",
                                                                                "Habsiguda",
                                                                                "Hanamkonda",
                                                                                "Himayatnagar",
                                                                                "Karimnagar",
                                                                                "Karmanghat",
                                                                                "Kasba",
                                                                                "Kasba Branch",
                                                                                "Kattedan",
                                                                                "Kavuri Hills",
                                                                                "Khammam",
                                                                                "Kharkhana",
                                                                                "Kokapet",
                                                                                "Kompally",
                                                                                "Kondapur",
                                                                                "KPHB-Malaysian",
                                                                                "Krishnanagar-Kol",
                                                                                "Kukatpally",
                                                                                "Kurnool",
                                                                                "L B Nagar",
                                                                                "Madhapur-2",
                                                                                "Madinaguda",
                                                                                "Mahabubnagar",
                                                                                "Manchieral",
                                                                                "Manikonda",
                                                                                "Mehdipatnam",
                                                                                "Motinagar",
                                                                                "Musheerabad",
                                                                                "Nallagandla",
                                                                                "Nallakunta",
                                                                                "Nandyal",
                                                                                "Nellore",
                                                                                "Nizamabad",
                                                                                "Nizampet",
                                                                                "Old Bowenpally",
                                                                                "Ongole",
                                                                                "Panjagutta",
                                                                                "Pragathi Nagar",
                                                                                "Rajahmundry",
                                                                                "Ramji Nagar",
                                                                                "Reporting",
                                                                                "Safilguda",
                                                                                "Santosh Nagar",
                                                                                "Sec II S D Road",
                                                                                "Seethammadhra",
                                                                                "Shah-Ali-Banda",
                                                                                "Shaikpet",
                                                                                "Shamshabad",
                                                                                "Sujathanagar",
                                                                                "Suncity",
                                                                                "Tele Reporting",
                                                                                "Tirupathi",
                                                                                "Tolichowki",
                                                                                "Vaidehi Nagar",
                                                                                "Vanasthalipuram",
                                                                                "Vasavi Colony",
                                                                                "Vijayanagar",
                                                                                "Vizag",
                                                                                "Vizag (Gajuwaka)",
                                                                                "Warangal",
                                                                                "Yapral",
                                                                                "Yousufguda"
                                                                           ])
                                                                      }
                                                                 />
                                                                 AP & Telangana
                                                                 <span className="region-badge">
                                                                      {(selectedRegions.regionsData["AP & Telangana"] || []).length}/79
                                                                 </span>
                                                            </label>
                                                       </div>
                                                       <div className="branch-list" style={{
                                                            maxHeight: "300px",
                                                            overflowY: "auto",
                                                            paddingRight: "6px"
                                                       }}>
                                                            {[
                                                                 "A S Rao Nagar",
                                                                 "Akruthi Township",
                                                                 "Alwal",
                                                                 "Ameerpet",
                                                                 "Attapur",
                                                                 "Balapur",
                                                                 "Bandlaguda",
                                                                 "BHEL",
                                                                 "BN Reddy Nagar",
                                                                 "Boduppal",
                                                                 "Chintal",
                                                                 "Dammaiguda",
                                                                 "Dilsukhnagar",
                                                                 "Dwarakanagar",
                                                                 "Film Nagar",
                                                                 "Gachibowli",
                                                                 "Gajuwaka",
                                                                 "Gopalapatnam",
                                                                 "Habsiguda",
                                                                 "Hanamkonda",
                                                                 "Himayatnagar",
                                                                 "Karimnagar",
                                                                 "Karmanghat",
                                                                 "Kasba",
                                                                 "Kasba Branch",
                                                                 "Kattedan",
                                                                 "Kavuri Hills",
                                                                 "Khammam",
                                                                 "Kharkhana",
                                                                 "Kokapet",
                                                                 "Kompally",
                                                                 "Kondapur",
                                                                 "KPHB-Malaysian",
                                                                 "Krishnanagar-Kol",
                                                                 "Kukatpally",
                                                                 "Kurnool",
                                                                 "L B Nagar",
                                                                 "Madhapur-2",
                                                                 "Madinaguda",
                                                                 "Mahabubnagar",
                                                                 "Manchieral",
                                                                 "Manikonda",
                                                                 "Mehdipatnam",
                                                                 "Motinagar",
                                                                 "Musheerabad",
                                                                 "Nallagandla",
                                                                 "Nallakunta",
                                                                 "Nandyal",
                                                                 "Nellore",
                                                                 "Nizamabad",
                                                                 "Nizampet",
                                                                 "Old Bowenpally",
                                                                 "Ongole",
                                                                 "Panjagutta",
                                                                 "Pragathi Nagar",
                                                                 "Rajahmundry",
                                                                 "Ramji Nagar",
                                                                 "Reporting",
                                                                 "Safilguda",
                                                                 "Santosh Nagar",
                                                                 "Sec II S D Road",
                                                                 "Seethammadhra",
                                                                 "Shah-Ali-Banda",
                                                                 "Shaikpet",
                                                                 "Shamshabad",
                                                                 "Sujathanagar",
                                                                 "Suncity",
                                                                 "Tele Reporting",
                                                                 "Tirupathi",
                                                                 "Tolichowki",
                                                                 "Vaidehi Nagar",
                                                                 "Vanasthalipuram",
                                                                 "Vasavi Colony",
                                                                 "Vijayanagar",
                                                                 "Vizag",
                                                                 "Vizag (Gajuwaka)",
                                                                 "Warangal",
                                                                 "Yapral",
                                                                 "Yousufguda"

                                                            ].map(
                                                                 (branch) => (
                                                                      <label key={branch} className="branch-checkbox">
                                                                           <input
                                                                                type="checkbox"
                                                                                checked={(selectedRegions.regionsData["AP & Telangana"] || []).includes(branch)}
                                                                                onChange={() => handleBranchToggle("AP & Telangana", branch)}
                                                                           />
                                                                           {branch}
                                                                      </label>
                                                                 )
                                                            )}
                                                       </div>
                                                  </div>

                                                  {/* Kolkata */}
                                                  <div className="region-card">
                                                       <div className="region-card-header">
                                                            <label className="region-select-all">
                                                                 <input
                                                                      type="checkbox"
                                                                      checked={(selectedRegions.regionsData["Kolkata"] || []).length === 5}
                                                                      onChange={() =>
                                                                           handleSelectAll("Kolkata", [
                                                                                "VIP Road",
                                                                                "Barasat",
                                                                                "Kasba",
                                                                                "Krishna Nagar",
                                                                                "Medinova",
                                                                           ])
                                                                      }
                                                                 />
                                                                 Kolkata
                                                                 <span className="region-badge">
                                                                      {(selectedRegions.regionsData["Kolkata"] || []).length}/5
                                                                 </span>
                                                            </label>
                                                       </div>
                                                       <div className="branch-list">
                                                            {["VIP Road", "Barasat", "Kasba", "Krishna Nagar", "Medinova"].map((branch) => (
                                                                 <label key={branch} className="branch-checkbox">
                                                                      <input
                                                                           type="checkbox"
                                                                           checked={(selectedRegions.regionsData["Kolkata"] || []).includes(branch)}
                                                                           onChange={() => handleBranchToggle("Kolkata", branch)}
                                                                      />
                                                                      {branch}
                                                                 </label>
                                                            ))}
                                                       </div>
                                                  </div>

                                                  {/* Pune */}
                                                  <div className="region-card">
                                                       <div className="region-card-header">
                                                            <label className="region-select-all">
                                                                 <input
                                                                      type="checkbox"
                                                                      checked={(selectedRegions.regionsData["Pune"] || []).length === 11}
                                                                      onChange={() =>
                                                                           handleSelectAll("Pune", [
                                                                                "Satara",
                                                                                "Hadapsar",
                                                                                "Mayur Colony",
                                                                                "Kalyani Nagar",
                                                                                "Chinchwad",
                                                                                "Dhanori",
                                                                                "Hadapsar",
                                                                                "Kalyaninagar",
                                                                                "Kondhawa",
                                                                                "Mayur",
                                                                                "Pimple Saudagar",
                                                                                "Gurgaon"
                                                                           ])
                                                                      }
                                                                 />
                                                                 Pune
                                                                 <span className="region-badge">
                                                                      {(selectedRegions.regionsData["Pune"] || []).length}/12
                                                                 </span>
                                                            </label>
                                                       </div>
                                                       <div className="branch-list">
                                                            {["Satara",
                                                                 "Hadapsar",
                                                                 "Mayur Colony",
                                                                 "Kalyani Nagar",
                                                                 "Chinchwad",
                                                                 "Dhanori",
                                                                 "Hadapsar",
                                                                 "Kalyaninagar",
                                                                 "Kondhawa",
                                                                 "Mayur",
                                                                 "Pimple Saudagar",
                                                                 "Gurgaon"
                                                            ].map((branch) => (
                                                                 <label key={branch} className="branch-checkbox">
                                                                      <input
                                                                           type="checkbox"
                                                                           checked={(selectedRegions.regionsData["Pune"] || []).includes(branch)}
                                                                           onChange={() => handleBranchToggle("Pune", branch)}
                                                                      />
                                                                      {branch}
                                                                 </label>
                                                            ))}
                                                       </div>
                                                  </div>

                                                  {/* Bangalore */}
                                                  <div className="region-card">
                                                       <div className="region-card-header">
                                                            <label className="region-select-all">
                                                                 <input
                                                                      type="checkbox"
                                                                      checked={(selectedRegions.regionsData["Bangalore"] || []).length === 3}
                                                                      onChange={() =>
                                                                           handleSelectAll("Bangalore", [
                                                                                "HSR Layout",
                                                                                "Yelahanka",
                                                                                "Kalaburagi-Gulbarga"
                                                                           ])
                                                                      }
                                                                 />
                                                                 Bangalore
                                                                 <span className="region-badge">
                                                                      {(selectedRegions.regionsData["Bangalore"] || []).length}/3
                                                                 </span>
                                                            </label>
                                                       </div>
                                                       <div className="branch-list">
                                                            {["HSR Layout", "Yelahanka", "Kalaburagi-Gulbarga"].map((branch) => (
                                                                 <label key={branch} className="branch-checkbox">
                                                                      <input
                                                                           type="checkbox"
                                                                           checked={(selectedRegions.regionsData["Bangalore"] || []).includes(branch)}
                                                                           onChange={() => handleBranchToggle("Bangalore", branch)}
                                                                      />
                                                                      {branch}
                                                                 </label>
                                                            ))}
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>


                         </div>
                         <br />
                         <div className="form-section" style={cardStyle}>
                              <div className="section-title" style={{ marginBottom: "5px" }}>
                                   Personality Assessment
                              </div>

                              <div style={grid6Columns}>
                                   <label>
                                        Attitude | Commitment to offered
                                        <select
                                             className="form-control input-form"

                                             name="attitude"
                                             value={personality.attitude}
                                             onChange={handlePersonalityChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             {personalityOptions.map((opt) => (
                                                  <option key={opt} value={opt}>
                                                       {opt}
                                                  </option>
                                             ))}
                                        </select>
                                        {errors["personality.attitude"] && (
                                             <div className="field-error">{errors["personality.attitude"]}</div>
                                        )}
                                   </label>

                                   {/* <label>
                Relevant Job Knowledge
                <select
                  className="form-control input-form"

                  name="jobKnowledge"
                  value={personality.jobKnowledge}
                  onChange={handlePersonalityChange}
                  style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                >
                  <option value="">-- Select --</option>
                  {personalityOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {errors["personality.jobKnowledge"] && (
                  <div className="field-error">{errors["personality.jobKnowledge"]}</div>
                )}
              </label> */}

                                   <label>
                                        Communication Skills
                                        <select
                                             className="form-control input-form"

                                             name="communication"
                                             value={personality.communication}
                                             onChange={handlePersonalityChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             {personalityOptions.map((opt) => (
                                                  <option key={opt} value={opt}>
                                                       {opt}
                                                  </option>
                                             ))}
                                        </select>
                                        {errors["personality.communication"] && (
                                             <div className="field-error">{errors["personality.communication"]}</div>
                                        )}
                                   </label>

                                   <label>
                                        Confidence
                                        <select
                                             className="form-control input-form"

                                             name="confidence"
                                             value={personality.confidence}
                                             onChange={handlePersonalityChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             {personalityOptions.map((opt) => (
                                                  <option key={opt} value={opt}>
                                                       {opt}
                                                  </option>
                                             ))}
                                        </select>
                                        {errors["personality.confidence"] && (
                                             <div className="field-error">{errors["personality.confidence"]}</div>
                                        )}
                                   </label>

                                   <label>
                                        Personality
                                        <select
                                             className="form-control input-form"

                                             name="personality"
                                             value={personality.personality}
                                             onChange={handlePersonalityChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             {personalityOptions.map((opt) => (
                                                  <option key={opt} value={opt}>
                                                       {opt}
                                                  </option>
                                             ))}
                                        </select>
                                        {errors["personality.personality"] && (
                                             <div className="field-error">{errors["personality.personality"]}</div>
                                        )}
                                   </label>

                                   <label>
                                        Flexibility to work at different
                                        <select
                                             className="form-control input-form"

                                             name="flexibility"
                                             value={personality.flexibility}
                                             onChange={handlePersonalityChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             {personalityOptions.map((opt) => (
                                                  <option key={opt} value={opt}>
                                                       {opt}
                                                  </option>
                                             ))}
                                        </select>
                                        {errors["personality.flexibility"] && (
                                             <div className="field-error">{errors["personality.flexibility"]}</div>
                                        )}
                                   </label>
                              </div>
                         </div>

                    </div>
               </div>



               {/* Form Actions */}
               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0px" }}>
                    <div className="small-muted">All fields are mandatory.</div>
                    <div className="form-actions">
                         <button
                              type="button"
                              className="btn"
                              style={{ background: "#9CA3AF" }}
                              onClick={handleCancel}
                              disabled={isSubmitting}
                         >
                              Cancel
                         </button>
                         <button
                              type="submit"
                              className="btn btn-warning pull-right"
                              disabled={isSubmitting}
                         >
                              {isSubmitting ? "Updating..." : "Update Assessment"}
                         </button>
                    </div>
               </div>
          </form>
     );
}